/**
 * Exact arithmetic on the decimal strings the API returns.
 *
 * Money crosses the wire as an exact string — `"18000.00"`, `"388800.00"` —
 * because a `DECIMAL` does not survive a float. `Number("18000.10") * 100` is
 * not `1801000`, and a basket total built that way disagrees with the database
 * by a cent or two, which is exactly the disagreement Xero and the reports will
 * surface. So every operation here works on scaled `BigInt` integers and never
 * touches a `number` except for an integer percentage.
 *
 * Everything is null-tolerant in the same way the API is: `null` means "no
 * value", not zero, and it propagates rather than being silently counted as 0.
 */

interface Scaled {
  /** The value with its decimal point removed. */
  units: bigint;
  /** How many digits were after the point. */
  scale: number;
}

function parseDecimal(value: string): Scaled | null {
  const trimmed = value.trim();
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return null;

  const negative = trimmed.startsWith('-');
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const [whole = '0', fraction = ''] = unsigned.split('.');
  const units = BigInt(whole + fraction);
  return { units: negative ? -units : units, scale: fraction.length };
}

/** Renders scaled units back to a fixed-point string. */
function formatScaled({ units, scale }: Scaled): string {
  const negative = units < 0n;
  const digits = (negative ? -units : units).toString().padStart(scale + 1, '0');
  const body =
    scale === 0
      ? digits
      : `${digits.slice(0, digits.length - scale)}.${digits.slice(digits.length - scale)}`;
  return negative ? `-${body}` : body;
}

/** Brings a value up to a wider scale without losing exactness. */
function rescale(value: Scaled, scale: number): bigint {
  return value.units * 10n ** BigInt(scale - value.scale);
}

/** Integer division rounding half away from zero, which is what money expects. */
function divideRoundHalf(numerator: bigint, denominator: bigint): bigint {
  const negative = numerator < 0n !== denominator < 0n;
  const a = numerator < 0n ? -numerator : numerator;
  const b = denominator < 0n ? -denominator : denominator;
  const quotient = (a * 2n + b) / (b * 2n);
  return negative ? -quotient : quotient;
}

/**
 * Sums decimal strings exactly.
 *
 * `null` entries are skipped rather than counted as zero — an unpriced line
 * contributes nothing to the basket but is not itself a zero. Returns `null`
 * when there is nothing at all to add, so a caller can tell "no total" from a
 * total of zero, and skips any entry that is not a decimal rather than poisoning
 * the whole sum with `NaN`.
 */
export function sumDecimalStrings(values: (string | null | undefined)[]): string | null {
  const parsed = values
    .map((value) => (typeof value === 'string' ? parseDecimal(value) : null))
    .filter((value): value is Scaled => value !== null);

  if (parsed.length === 0) return null;

  const scale = parsed.reduce((widest, value) => Math.max(widest, value.scale), 0);
  const units = parsed.reduce((running, value) => running + rescale(value, scale), 0n);
  return formatScaled({ units, scale });
}

/**
 * Splits a flat line amount across a quantity, for seeding a document line.
 *
 * A future billable plans **a flat amount for the line** — owner's decision,
 * 2026-09-03 — while a quote or invoice line stores a UNIT PRICE that its
 * quantity multiplies. Raising the plan therefore has to divide, and division
 * is the one money operation here that cannot always be exact.
 *
 * ⚠️ **`exact` IS THE POINT OF THE RETURN, NOT A DETAIL OF IT.** `10000.00`
 * over 3 gives a unit price of `3333.33`, and the line then reads `9999.99` —
 * a penny under a figure somebody planned and will be billed against. That is
 * small, silent, and exactly the class of drift this module exists to refuse,
 * so the caller is handed the fact rather than left to notice it. **Do not
 * discard `exact` at a call site**; say it on screen.
 *
 * ⚠️ **Rounds HALF AWAY FROM ZERO, like every other figure here**, through the
 * same `divideRoundHalf` the discount and tax previews use. It deliberately
 * does not floor: flooring would make the seeded line quietly cheaper than the
 * plan every time it was inexact, always in the customer's favour and never
 * ours, which is a bias rather than a rounding rule.
 *
 * ⚠️ **The amount is SIGNED and the quantity is not.** A plan may be negative
 * (a credit note), so this must not borrow `centUnits`, which refuses one —
 * "nothing on a LINE may be negative" is a rule about line items, and this
 * operates on the plan side of the wire.
 *
 * Answers `null` — never a guess — when the amount is not a decimal, when it
 * carries more than two decimal places (a third decimal is a refusal for the
 * caller to report, not a value to reshape), or when the quantity is not a
 * whole number of at least 1. A quantity of zero is not a division to round; it
 * is a question with no answer.
 */
export function splitAmountOverQuantity(
  amount: string,
  quantity: number,
): { unitPrice: string; exact: boolean } | null {
  const parsed = parseDecimal(amount);
  if (parsed === null || parsed.scale > 2) return null;
  if (!Number.isInteger(quantity) || quantity < 1) return null;

  const cents = rescale(parsed, 2);
  const divisor = BigInt(quantity);
  const unitCents = divideRoundHalf(cents, divisor);
  return {
    unitPrice: formatScaled({ units: unitCents, scale: 2 }),
    exact: unitCents * divisor === cents,
  };
}

/**
 * What a typed line AMOUNT has to change to become true.
 *
 * The Amount column is `line_subtotal`, a GENERATED STORED column — so it can
 * never be sent. Typing into it is therefore not a write but a QUESTION: which
 * writable field, changed to what, makes the line come out at this figure?
 * This is the one place that question is answered, shared by all three grids.
 *
 * ⚠️ **THE TARGET IS THE PRE-TAX SUBTOTAL, owner's decision 2026-09-12.** Tax
 * is charged on the already-rounded subtotal, so solving backwards from a
 * tax-INCLUSIVE total is not always possible to the penny — at 20% the reachable
 * totals step in 1.2p, and a typed figure between two of them can only be
 * approximated. Against the subtotal every answer here is exact, which is why
 * the box was defined this way rather than as "what the customer pays".
 *
 * The three-way rule the owner asked for — price, discount, amount, fill two and
 * the third follows — falls out of WHICH ONE IS MISSING:
 *
 *   no price yet   -> the amount SETS the price      (amount / quantity)
 *   a price set    -> the amount sets the DISCOUNT   (quantity × price − amount)
 *
 * 🔴 **THE DISCOUNT IS SOLVED AS AN AMOUNT UNLESS `keepPercentage` SAYS
 * OTHERWISE — owner's instruction, 2026-09-13: *"on line item amount change the
 * discount type should not change and be calculated in the type it is in."***
 * ⚠️ *SUPERSEDED, and read it before relaxing anything here: "**The discount is
 * solved as an AMOUNT, never as a percentage**, and that is not a preference …
 * Solving into `discountAmount` lands on the typed figure to the penny, always.
 * The caller flips the line's mode to match and says so; it must never write a
 * rounded percentage instead."*
 *
 * ⚠️ **THE ARITHMETIC THAT MOTIVATED THE OLD RULE IS UNCHANGED, AND IT IS WHY
 * `discount-percentage` CARRIES AN `exact` FLAG AND ITS OWN `amount`.**
 * `discount` is `DECIMAL(5,2)`, so the subtotals a percentage can express are
 * `quantity × unitPrice × 0.0001` apart — **£1.80 on an 18,000 line**. Type
 * 17,000 against an 18,000 gross and the nearest percentage is 5.56%, which is a
 * discount of 1,000.80 and a line of **16,999.20**. The figure the line will
 * actually come to is therefore RETURNED rather than implied, and the owner's
 * ruling is that the caller SAYS SO. ⛔ **Never round it away, and never write a
 * percentage without reading `exact`** — that is the loss migration 111 exists
 * to have removed and `percentageForAmount()` was deleted for.
 *
 * ⚠️ **`keepPercentage` MEANS "A MODE WAS CHOSEN", NOT "the mode reads as
 * percentage".** A line with no discount at all reads as percentage mode —
 * that is what `discountAmount === null` means — so passing `true` for every
 * untouched line would make the commonest edit in the grid inexact. Owner's
 * decision: a line nobody has discounted has no mode to preserve. The predicate
 * is `percentageModeIsChosen()` in `@/lib/discount-mode`, which is the one place
 * that question is answered; this module deliberately takes a plain boolean so
 * the pure arithmetic here imports no UI vocabulary.
 *
 * ⛔ **`percentage-unreachable` IS NOT A ROUNDING FAILURE, IT IS A COLUMN THAT
 * CANNOT EXPRESS THE ANSWER.** `discount` is `CHECK (0..100)`, so on a gross of
 * zero or below no percentage produces any discount at all: a deduction line's
 * gross is negative and a percentage of it would make the line LESS negative,
 * which is the direction `discountAmount`'s own non-negative CHECK forbids. The
 * caller reports it; it must not silently fall back to the amount column, which
 * would change the mode — the exact thing this option exists to stop.
 *
 * ⚠️ **`above-gross` is a REFUSAL, not a negative discount.** `discount_amount`
 * carries `CHECK (… >= 0)`, so an amount above quantity × price cannot be
 * expressed by discounting — the price itself has to rise. It answers with the
 * gross so the caller can say which figure the amount has to come under, rather
 * than "invalid".
 *
 * ⚠️ **`exact: false` is the point of that variant, not a detail.** 10,000 over
 * 3 gives 3,333.33 a unit and a line of 9,999.99 — a penny under what somebody
 * typed. The caller SAYS SO; it does not quietly round, and it does not discard
 * the flag.
 *
 * `null` — never a guess — for anything not yet worth solving: a quantity that
 * is not a whole number of at least 1, or an amount that is not a non-negative
 * figure at two decimal places. Those are the form's own refusals to report.
 *
 * ⚠️ **A NEGATIVE AMOUNT IS ACCEPTED — a DEDUCTION LINE.** Migration 171 took
 * the `ROUND(GREATEST(…, 0), 2)` clamp off all seven generated money columns
 * and the API's `UNIT_PRICE_PATTERN` gained a `-?`, so a negative line is now
 * stored as typed rather than rewritten to `0.00`. The three layers moved
 * together, deliberately: with only this one relaxed, a deduction line would
 * have saved cleanly, reported success and billed nothing.
 *
 * ⚠️ **On a negative line `above-gross` fires in the direction that reads
 * backwards, and it is still right.** `discountAmount` is non-negative by
 * CHECK, so from a gross of −4,150 a discount can only make the line MORE
 * negative — asking for −4,000 is asking for a smaller deduction, which the
 * price has to provide. The caller names the gross either way.
 */
export type LineAmountSolution =
  | { kind: 'discount-amount'; discountAmount: string }
  /**
   * Solved into the PERCENTAGE column, keeping a mode somebody chose.
   *
   * ⚠️ **`amount` is what the line will ACTUALLY come to, and it is not always
   * the figure that was typed.** It is computed with the same one-division
   * round-half rule `computeGeneratedLinePreview` uses for the percentage step,
   * so the note this drives and the preview beside it cannot disagree.
   * `exact` is `amount === the typed figure`.
   */
  | { kind: 'discount-percentage'; discountPercent: string; amount: string; exact: boolean }
  /** Percentage mode was asked for on a gross of zero or below. See above. */
  | { kind: 'percentage-unreachable'; gross: string }
  | { kind: 'unit-price'; unitPrice: string; exact: boolean }
  | { kind: 'above-gross'; gross: string };

export function solveLineAmount(input: {
  /** A whole number of units, as typed. */
  quantity: string;
  /** The line's price, or `null`/`''` when it has none yet. */
  unitPrice: string | null;
  /** The pre-tax subtotal the reader typed. */
  amount: string;
  /**
   * Solve into `discount` rather than `discountAmount`, because the reader has
   * already chosen percentage mode on this line.
   *
   * ⚠️ **Ask `percentageModeIsChosen()` for this; do not pass
   * `discountAmount === null`.** That test is true of every untouched line.
   */
  keepPercentage?: boolean;
}): LineAmountSolution | null {
  const quantity = input.quantity.trim();
  if (!/^\d+$/.test(quantity)) return null;
  const qty = BigInt(quantity);
  if (qty < 1n) return null;

  /*
   * ⚠️ **SIGNED, and deliberately not `centUnits`** — which refuses a negative,
   * because it exists for `discountAmount`, where a negative would be a
   * surcharge. A line AMOUNT may be negative since migration 171. The scale
   * check is kept: a third decimal is a refusal for the caller to report, never
   * a figure to quietly reshape.
   */
  const amount = parseDecimal(input.amount.trim());
  if (amount === null || amount.scale > 2) return null;
  const amountUnits = rescale(amount, 2);

  /*
   * No price on the line: the amount is what sets one. `splitAmountOverQuantity`
   * already does this division — including the `exact` flag and the round-half-
   * away-from-zero rule — so it is reused rather than repeated. A second copy of
   * a division would agree on the day it was written and drift at the first
   * rounding correction.
   */
  const price = input.unitPrice === null ? '' : input.unitPrice.trim();
  if (price === '') {
    const split = splitAmountOverQuantity(input.amount.trim(), Number(quantity));
    return split === null ? null : { kind: 'unit-price', ...split };
  }

  // Signed for the same reason the amount is: a deduction line's price is one.
  const parsedPrice = parseDecimal(price);
  if (parsedPrice === null || parsedPrice.scale > 2) return null;
  const priceUnits = rescale(parsedPrice, 2);

  /*
   * Gross is exact: both operands are integers in cent units, so there is no
   * rounding here at all and the subtraction below cannot drift. This is the
   * same `quantity * unit_price` the generated column computes before it applies
   * any discount.
   */
  const gross = priceUnits * qty;
  if (amountUnits > gross) {
    return { kind: 'above-gross', gross: formatScaled({ units: gross, scale: 2 }) };
  }

  const discountUnits = gross - amountUnits;

  if (input.keepPercentage) {
    /*
     * ⛔ **A percentage cannot discount a gross of zero or below.** `discount`
     * is `CHECK (0..100)`, so every percentage of a non-positive gross is zero
     * or moves the line the way the non-negative `discountAmount` CHECK
     * forbids. Reported rather than silently solved into the amount column: a
     * silent fallback would change the mode, which is the one thing this
     * branch exists to prevent.
     */
    if (gross <= 0n) {
      return { kind: 'percentage-unreachable', gross: formatScaled({ units: gross, scale: 2 }) };
    }

    /*
     * `DECIMAL(5,2)` in its own units — 100% at scale 2 — which is exactly the
     * `discountFull` `computeGeneratedLinePreview` builds. The two must agree:
     * this solves for the percentage that makes THAT preview land on the typed
     * figure, so a second spelling of the scale would put the note and the
     * preview a penny apart on the same row.
     */
    const percentFull = 10_000n;
    const percentUnits = divideRoundHalf(discountUnits * percentFull, gross);
    /*
     * Read straight back through the preview's own step — ONE division of
     * `gross × (100% − p)`, not two — so `exact` is a measurement of what the
     * server will store rather than a claim about the division above. `exact`
     * is false whenever the percentage column cannot express the typed figure,
     * which on a large line is most of the time.
     */
    const reached = divideRoundHalf(gross * (percentFull - percentUnits), percentFull);
    return {
      kind: 'discount-percentage',
      discountPercent: formatScaled({ units: percentUnits, scale: 2 }),
      amount: formatScaled({ units: reached, scale: 2 }),
      exact: reached === amountUnits,
    };
  }

  return {
    kind: 'discount-amount',
    discountAmount: formatScaled({ units: discountUnits, scale: 2 }),
  };
}

/**
 * `amount × percent / 100`, rounded to the amount's own scale.
 *
 * This is expected revenue. Both operands can legitimately be absent — an
 * unpriced deal, or one with no stage — and the answer is then `null` rather
 * than `0`: "unknown" and "nothing" are different things to show a user.
 */
export function percentOfDecimal(
  amount: string | null,
  percent: number | null,
): string | null {
  if (amount === null || percent === null || !Number.isFinite(percent)) return null;
  const parsed = parseDecimal(amount);
  if (parsed === null) return null;

  /*
   * The percentage is scaled to an integer rather than rounded to one. Deal
   * probabilities are whole numbers, but invoice tax and discount rates are
   * DECIMAL(5,2) and 12.5% is entirely legal — rounding it to 13% here would
   * be a silently wrong number on a financial record.
   *
   * `String(12.5)` is "12.5", which parses to 125 at scale 1, so dividing by
   * 100 × 10^scale keeps the whole thing in exact integer arithmetic.
   */
  const scaledPercent = parseDecimal(String(percent));
  if (scaledPercent === null) return null;

  return formatScaled({
    units: divideRoundHalf(
      parsed.units * scaledPercent.units,
      100n * 10n ** BigInt(scaledPercent.scale),
    ),
    scale: parsed.scale,
  });
}

/**
 * A non-negative money string as exact cent units, or `null`.
 *
 * More than two decimal places answers `null` rather than rounding: a third
 * decimal in a money box is a refusal for the caller to report, not a value to
 * quietly reshape. A negative answers `null` too — nothing on a line may be one.
 */
function centUnits(value: string): bigint | null {
  const parsed = parseDecimal(value);
  if (parsed === null || parsed.units < 0n || parsed.scale > 2) return null;
  return rescale(parsed, 2);
}

/**
 * The percentage to show for a deal: its STAGE's, and nothing else.
 *
 * ⚠️ **The per-deal override is gone — owner's decision, 2026-08-31.** This
 * used to read `ownOverride ?? stageProbability`, and `d_deals.probability` was
 * that override. The rule now is that a deal's probability is a property of
 * where it sits in the pipeline, so a stage change always moves the forecast
 * with it and no deal can be pinned out of step with its own stage.
 *
 * ⚠️ **The column still EXISTS and the API still accepts it** — `PATCH /deals`
 * declares `probability: z.number().int().min(0).max(100).nullish()`, and a
 * deal written before this decision may still hold a non-null value. Nothing in
 * this app writes one any more (the key is absent from `DealFormModal`'s
 * `FormValues` and from `InlineDealFields`' `ValueKey`, so no payload here can
 * express it), and nothing reads one: a stored override is now IGNORED rather
 * than cleared. So the figure on screen can differ from what it showed
 * yesterday for such a deal, which is the intended change, not a regression.
 *
 * `undefined` is accepted alongside `null` because `deal.stage` is optional and
 * callers reach through it; a deal with no stage has no percentage, and the
 * answer is `null` rather than `0` — "unknown" and "nothing" are different
 * things to show a user.
 */
export function effectiveProbability(
  stageProbability: number | null | undefined,
): number | null {
  return stageProbability ?? null;
}

/** The three columns the database generates for a quote or invoice line. */
export interface GeneratedLinePreview {
  lineSubtotal: string;
  lineTax: string;
  lineTotal: string;
}

/** The five line fields the preview reads, exactly as a form holds them. */
export interface GeneratedLineInput {
  /** A whole number of units, as typed. */
  quantity: string;
  /** Already resolved: the typed price, else the catalogue snapshot, else null. */
  unitPrice: string | null;
  /** A percentage, as typed. Blank means the server's default of 0. */
  discount: string;
  /**
   * An EXACT money discount, as typed, or `null` for "this line discounts by
   * percentage" — which is what almost every line does.
   *
   * ⚠️ **REQUIRED, and deliberately not optional.** An optional member is a
   * language feature for making an absence legal, and the absence that matters
   * here is a caller that never wired the field through: `tsc` catches that on
   * every build, where an optional one would preview a line at full price with
   * everything green. A caller with genuinely nothing to pass writes `null`,
   * which is a line a reviewer can see.
   *
   * ⚠️ **The two are mutually exclusive on the wire** — sending both non-zero
   * is a 422 naming `discountAmount` — so in practice one of this and
   * `discount` is always inert. The preview applies both anyway rather than
   * branching, because that is the arithmetic and it needs no rule.
   */
  discountAmount: string | null;
  /** A percentage, as typed. Blank means the server's default of 0. */
  taxRate: string;
}

/**
 * A non-negative decimal string, scaled.
 *
 * Nothing on a line is ever negative — a negative quantity, price, discount or
 * tax rate is a 422 — so a leading `-` is refused here rather than quietly
 * previewing a figure the server would never store.
 */
function parseUnsignedDecimal(value: string): Scaled | null {
  const trimmed = value.trim();
  if (trimmed.startsWith('-')) return null;
  return parseDecimal(trimmed);
}

/**
 * Reproduces the three generated columns on a quote or invoice line.
 *
 * ⚠️ The ORDER is correctness, not style. The database computes:
 *
 *   lineSubtotal = round(quantity * unitPrice * (1 - discount/100), 2)
 *                    - discountAmount                     // exact, nothing to round
 *   lineTax      = round(lineSubtotal * taxRate/100, 2)   // on the ROUNDED subtotal
 *   lineTotal    = lineSubtotal + lineTax                 // sum of two rounded parts
 *
 * ⚠️ **`discountAmount` comes off the ROUNDED subtotal, i.e. exactly where the
 * percentage lands.** That is not a new rule: it is the arithmetic the retired
 * amount-to-percentage conversion was approximating, now done exactly. Both
 * money figures are at two decimal places already, so the subtraction rounds
 * nothing and `lineSubtotal + lineTax === lineTotal` still holds to the cent.
 *
 * so `lineSubtotal + lineTax === lineTotal` holds exactly, always. Round once
 * at the end instead — the obvious-looking simplification — and some rows come
 * out a cent away from the stored value, and the stored value is the one Xero
 * reads. All three columns are read-only server-side; this exists only to show
 * a figure before the save, and the server's answer always wins.
 *
 * Everything is scaled BigInt, for the reasons set out at the top of this file.
 * `percentOfDecimal` is deliberately not reused: it applies one percentage in
 * one rounded step, to the amount's own scale, and the three steps above are
 * not one step. The intermediate rounding has to happen *between* the two
 * percentages, which a single call cannot express. The final addition does go
 * through `sumDecimalStrings`, which is exactly the "add the two already-rounded
 * parts" the generated column performs.
 *
 * ⚠️ `unitPrice: null` returns `null`, not `"0.00"`. The server's columns
 * `COALESCE(unit_price, 0)`, so an unpriced line and a line priced at zero both
 * generate `"0.00"` and the columns cannot tell you afterwards which it was —
 * which is precisely why this refuses to assert one. A caller renders that
 * `null` as a sentence about the line having no price, never as three zeroes.
 *
 * `null` also comes back for anything not yet worth previewing: a quantity that
 * is not a whole number of at least 1, a malformed decimal, or a percentage
 * over 100. Those are all 422s the form's own validator already reports, and a
 * negative subtotal underneath that message would only add confusion.
 */
export function computeGeneratedLinePreview(
  input: GeneratedLineInput,
): GeneratedLinePreview | null {
  if (input.unitPrice === null) return null;

  const quantity = input.quantity.trim();
  if (!/^\d+$/.test(quantity)) return null;
  const qty = BigInt(quantity);
  if (qty < 1n) return null;

  /*
   * ⚠️ **THE PRICE IS SIGNED AND THE TWO PERCENTAGES ARE NOT.** A DEDUCTION
   * LINE is a negative unit price — migration 171, owner's instruction
   * 2026-09-12, from INV-0886's "Deduction for Cape Town Participation Fee" —
   * while a discount or a tax rate below zero remains a 422. Reading the price
   * through `parseUnsignedDecimal` here would preview a deduction as `null`,
   * i.e. as "no figure yet", on a line the server now stores perfectly well.
   */
  const price = parseDecimal(input.unitPrice.trim());
  // A blank percentage box means the server's default of 0, so preview it as 0.
  const discount = parseUnsignedDecimal(input.discount.trim() === '' ? '0' : input.discount);
  const taxRate = parseUnsignedDecimal(input.taxRate.trim() === '' ? '0' : input.taxRate);
  if (price === null || discount === null || taxRate === null) return null;

  /*
   * The money discount, in cent units. `null` on the INPUT means the line is not
   * discounted by amount at all, which is zero here; a malformed or negative
   * string is refused outright, exactly as a malformed percentage is, rather
   * than previewed as no discount — a figure that reads as a valid answer.
   *
   * A blank box is the same as absent: nothing has been entered yet.
   */
  const typedAmount = input.discountAmount === null ? '' : input.discountAmount.trim();
  const discountAmountUnits = typedAmount === '' ? 0n : centUnits(typedAmount);
  if (discountAmountUnits === null) return null;

  /*
   * 100% at each percentage's own scale, so 12.5 and 12.50 both compare and
   * divide exactly. Rounding either to a whole number would be a silently wrong
   * number on a financial record — a 12.5% tax rate is entirely legal here.
   */
  const discountFull = 100n * 10n ** BigInt(discount.scale);
  const taxFull = 100n * 10n ** BigInt(taxRate.scale);
  if (discount.units > discountFull || taxRate.units > taxFull) return null;

  /*
   * Step 1. quantity * price * (1 - discount/100), taken to 2dp in ONE exact
   * division rather than two — two would round twice and drift off the stored
   * value. The trailing 100n is what carries the result to cent units.
   */
  const percentagedUnits = divideRoundHalf(
    qty * price.units * (discountFull - discount.units) * 100n,
    10n ** BigInt(price.scale) * discountFull,
  );

  /*
   * Then the money discount, straight off it.
   *
   * ⚠️ **A NEGATIVE SUBTOTAL IS NOW PREVIEWED RATHER THAN SUPPRESSED, and this
   * line used to read `if (subtotalUnits < 0n) return null;`.** It was right
   * while the generated columns carried `ROUND(GREATEST(…, 0), 2)`: the server
   * would have stored `0.00`, so previewing a negative would have promised a
   * figure the database was about to refuse to keep. **Migration 171 dropped
   * that clamp**, so the negative is exactly what gets stored and showing it is
   * the honest answer.
   *
   * ⚠️ **It reaches here two ways and both are legitimate now** — a deduction
   * line's negative price, and a `discountAmount` larger than the line it sits
   * on. The second used to be the only way and was a mistake worth hiding; it
   * is now a credit, and the form's own validator is what speaks if it was not
   * meant.
   */
  const subtotalUnits = percentagedUnits - discountAmountUnits;

  // Step 2. Tax is charged on that already-rounded subtotal, never on the exact one.
  const taxUnits = divideRoundHalf(subtotalUnits * taxRate.units, taxFull);

  // Step 3. The sum of the two rounded parts, not a third rounded expression.
  const lineSubtotal = formatScaled({ units: subtotalUnits, scale: 2 });
  const lineTax = formatScaled({ units: taxUnits, scale: 2 });
  const lineTotal = sumDecimalStrings([lineSubtotal, lineTax]);
  if (lineTotal === null) return null;

  return { lineSubtotal, lineTax, lineTotal };
}

